import {
	formatPhone,
	Menu,
	MenuItem,
	MenuPopover,
	MenuPressable,
	MenuSection,
	MenuSeparator,
	MenuTrigger,
	P,
	Stack,
	SvgIcon,
	Text,
	useText,
} from "@sveabank/cc-common";
import Card from "../../components/card";
import Content from "../../components/content";
import Hide from "../../components/hide";
import { usePageContext } from "../../hooks/context";
import type { ConfigureCardState } from "../../types/step";
import CardContactForm, { type CardContactFormValues } from "./card-contact-form";
import { useApplicationSettings } from "./hooks";

type Props = {
	card: ConfigureCardState;
	displayName?: string;
	ssn?: string;
	onEditName: () => void;
	onEditCardOwner: () => void;
	setIsEdit: (toggle: boolean) => void;
};

const SelectedCardForm = ({
	card,
	displayName,
	ssn,
	setIsEdit,
	onEditName,
	onEditCardOwner,
}: Props) => {
	const { setHasFormError, applicationDetails, applicationId, configureMastercard } =
		usePageContext(useApplicationSettings);

	const [t, texts] = useText();

	const submitCardForm = async (values: CardContactFormValues) => {
		const tempName = displayName || card.displayName;
		const tempSsn = ssn || card.nationalId;

		if (tempName && tempSsn) {
			await configureMastercard({
				applicationId,
				cardSettings: {
					displayName: tempName,
					email: values.companyEmail,
					nationalId: tempSsn,
					phone: {
						countryCode: values.companyCountryCode,
						number: formatPhone(values.companyPhone),
					},
					productId: card.productId,
					accountId:
						card.name === "ConfigureMastercardDirectStep"
							? card.selectedAccountId || card.availableAccountIds[0]?.accountId
							: undefined,
				},
			}).unwrap();
			setIsEdit(false);
			setHasFormError(false);
		}
	};

	return (
		<Stack gap={8}>
			<Card classNames="bg-background-secondary" fullWidth>
				<Content>
					<Stack>
						<Stack direction="row" gap={4}>
							<Hide to="tablet">
								<P>{t(texts.applicationSettings.cardSettings.cardDisplayName)}</P>
							</Hide>
							<Text bold>{displayName || card.displayName}</Text>
						</Stack>
						<Stack direction="row" gap={4}>
							<Hide to="tablet">
								<P>{t(texts.applicationSettings.cardSettings.cardCompanyName)}</P>
							</Hide>
							<Text bold>{applicationDetails?.organization.orgName}</Text>
						</Stack>
					</Stack>
				</Content>
				<MenuTrigger>
					<MenuPressable label="Hantera kort">
						<SvgIcon imageSrc="EDIT" className="text-interactive-primary" height={20} />
					</MenuPressable>
					<MenuPopover placement="bottom end">
						<Menu>
							<MenuSection>
								<MenuItem
									onPress={() => {
										onEditName();
									}}
								>
									{t(texts.applicationSettings.buttonText.changeDisplayName)}
								</MenuItem>
								<MenuSeparator />
								<MenuItem
									onPress={() => {
										onEditCardOwner();
									}}
								>
									{t(texts.applicationSettings.buttonText.changeCardOwner)}
								</MenuItem>
							</MenuSection>
						</Menu>
					</MenuPopover>
				</MenuTrigger>
			</Card>

			<CardContactForm
				address={applicationDetails?.organization.address?.addressLine1}
				ssn={ssn}
				card={card}
				handleOnSubmit={submitCardForm}
			/>
		</Stack>
	);
};

export default SelectedCardForm;
